import React, { useEffect, useState } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js/pure";
import axios from "axios";
import { LeftOutlined } from "@ant-design/icons"; // Import the icon
import "./PaymentPage.css";

const stripeURL = process.env.REACT_APP_STRIPE_PUBLIC_KEY;

/*
 * Stripe.js is fetched on demand, NOT when this module is imported.
 *
 * Two separate things were causing a request to js.stripe.com on EVERY page
 * of the app - home, search results, sign-in, a public vendor listing, all
 * of it - for a script only the checkout page can ever use:
 *
 *   1. App.js imports PaymentPage eagerly, so this module's body runs on
 *      every page load, and loadStripe() was being called at module scope.
 *   2. The default "@stripe/stripe-js" entry point injects the Stripe.js
 *      <script> as a side effect of BEING IMPORTED, before anyone calls
 *      loadStripe at all. Confirmed in the installed 5.7.0: dist/index.js
 *      calls loadScript(null) at module scope, dist/pure.js does not. That
 *      is exactly what the /pure entry point exists for.
 *
 * Fixing only the first would have changed nothing, because the import
 * itself was doing the loading. Enough of those requests and Stripe answers
 * 429, which surfaces as "Failed to load Stripe.js" on the one page that
 * genuinely needs it.
 *
 * Memoised, so the payment page still shares one Stripe instance across
 * renders rather than re-loading on each pass.
 */
let stripePromise;

const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(stripeURL);
  }
  return stripePromise;
};

const ACCENT = "#3b82f6";

const stripeAppearance = {
  theme: "stripe",
  variables: {
    colorPrimary: ACCENT,
    colorText: "#111827",
    borderRadius: "10px",
    // Stripe renders PaymentElement in its own iframe, so host-page CSS
    // (including our media queries) can't reach its inputs. iOS Safari
    // zooms the page on focusing any input under 16px, so it's set here.
    fontSizeBase: "16px",
  },
};

const PaymentPage = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // `paymentIntentId` was also destructured here but never read anywhere in
  // the file, so it is dropped. Plans.jsx still sends it in navigate state
  // if a future flow needs it.
  const { clientSecret, plan } = location.state || {};
  const paymentStatus = searchParams.get("redirect_status");
  // Stripe Checkout Session flow return
  const sessionId = searchParams.get("session_id");
  const checkoutStatus = searchParams.get("status"); // "success"

  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Handle Stripe Checkout Session success redirect
    if (sessionId && checkoutStatus === "success") {
      setShowSuccess(true);
      setTimeout(() => {
        navigate("/subscription");
      }, 2500);
      return;
    }
    // Handle legacy PaymentIntent redirect
    if (paymentStatus === "succeeded") {
      setShowSuccess(true);
      setTimeout(() => {
        navigate("/subscription");
      }, 2500);
    }
  }, [paymentStatus, sessionId, checkoutStatus]);

  const handleGoBack = () => {
    navigate(-1);
  };

  return (
    <div style={styles.page}>
      <div style={styles.container}>
      <div className="payment-card">
        {plan && !showSuccess && (
          <div className="payment-summary">
            <button onClick={handleGoBack} style={styles.backLink}>
              <LeftOutlined style={{ fontSize: "12px" }} />
              Back to plans
            </button>

            <p style={styles.summaryEyebrow}>You're subscribing to</p>
            <h2 style={styles.summaryPlanName}>{plan.name}</h2>
            <p style={styles.summaryPrice}>
              ${plan.price}
              <span style={styles.summaryPriceUnit}>/month</span>
            </p>

            {Array.isArray(plan.description) && (
              <ul style={styles.summaryList}>
                {plan.description.map((item, i) => (
                  <li key={i} style={styles.summaryListItem}>
                    <span style={styles.summaryCheck}>&#10003;</span>
                    {item}
                  </li>
                ))}
              </ul>
            )}

            <div style={styles.summaryFooter}>
              <p style={styles.summaryReassurance}>
                Cancel anytime. No long-term lock-in.
              </p>
              <p style={styles.summaryReassurance}>
                Payments are encrypted and processed securely by Stripe.
              </p>
            </div>
          </div>
        )}

        <div className="payment-form-panel">
          {!plan && (
            <button onClick={handleGoBack} style={styles.backLink}>
              <LeftOutlined style={{ fontSize: "12px" }} />
              Back
            </button>
          )}

          {showSuccess ? (
            <div style={styles.successWrap}>
              <div style={styles.successIcon}>&#10003;</div>
              <p style={styles.successMessage}>Payment successful</p>
              {/* Kept honey-new's wording here: it says what actually
                  happened and where the user is going. */}
              <p style={styles.successSubtext}>
                Your subscription is now active. Redirecting to your account
                &hellip;
              </p>
            </div>
          ) : clientSecret ? (
            <>
              <h2 style={styles.heading}>Payment details</h2>
              <p style={styles.subheading}>
                You'll be charged the amount above once confirmed.
              </p>
              <Elements
                stripe={getStripe()}
                options={{ clientSecret, appearance: stripeAppearance }}
              >
                <CheckoutForm amount={plan?.price} />
              </Elements>
            </>
          ) : (
            <>
              <h2 style={styles.heading}>Complete your payment</h2>
              <p style={styles.error}>
                Payment information is missing. Please go back and try again.
              </p>
            </>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

const CheckoutForm = ({ amount }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setErrorMessage("");

    if (!stripe || !elements) {
      setLoading(false);
      return;
    }

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/subscription`,
      },
      redirect: "if_required",
    });

    if (error) {
      console.error("Payment Error:", error.message);
      setErrorMessage(error.message);
      setLoading(false);
    } else if (paymentIntent && paymentIntent.status === "succeeded") {
      console.log("Payment succeeded:", paymentIntent);

      try {
        const token = localStorage.getItem("userToken");
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/auth/v1/payment/${paymentIntent.id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        console.log("Payment API Response:", response.data);
      } catch (error) {
        console.error("API Fetch Error:", error);
      }

      window.location.href = "/subscription";
    } else {
      console.log("Payment Intent Status:", paymentIntent);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      <div style={styles.cardElementContainer}>
        <PaymentElement />
      </div>
      {errorMessage && <p style={styles.error}>{errorMessage}</p>}
      <button type="submit" disabled={!stripe || loading} style={styles.button}>
        {loading
          ? "Processing..."
          : amount
          ? `Pay $${amount} Now`
          : "Pay Now"}
      </button>
    </form>
  );
};

const styles = {
  page: {
    width: "100%",
    minHeight: "calc(100vh - 72px)",
    backgroundColor: "#ffffff",
  },
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "calc(100vh - 72px)",
    boxSizing: "border-box",
    padding: "16px 20px",
  },
  summaryEyebrow: {
    margin: "20px 0 4px",
    fontSize: "12px",
    fontWeight: 600,
    letterSpacing: "0.02em",
    color: "#94a3b8",
  },
  summaryPlanName: {
    margin: "0 0 12px",
    fontSize: "22px",
    fontWeight: 700,
    color: "#fff",
  },
  summaryPrice: {
    margin: "0 0 20px",
    fontSize: "30px",
    fontWeight: 700,
    color: "#fff",
  },
  summaryPriceUnit: {
    fontSize: "14px",
    fontWeight: 500,
    color: "#94a3b8",
    marginLeft: "4px",
  },
  summaryList: {
    listStyle: "none",
    margin: "0 0 24px",
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  summaryListItem: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    fontSize: "13px",
    lineHeight: 1.5,
    color: "#e2e8f0",
  },
  summaryCheck: {
    color: "#4ade80",
    fontSize: "13px",
    marginTop: "1px",
  },
  summaryFooter: {
    marginTop: "auto",
    paddingTop: "20px",
    borderTop: "1px solid rgba(255,255,255,0.1)",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  summaryReassurance: {
    margin: 0,
    fontSize: "12px",
    color: "#94a3b8",
    lineHeight: 1.5,
  },
  backLink: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: 0,
    marginBottom: "10px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#94a3b8",
    background: "none",
    border: "none",
    cursor: "pointer",
  },
  heading: {
    margin: "0 0 4px",
    fontSize: "17px",
    fontWeight: 700,
    color: "#111827",
  },
  subheading: {
    margin: "0 0 12px",
    fontSize: "13px",
    color: "#6b7280",
    lineHeight: 1.5,
  },
  successWrap: { textAlign: "center", padding: "24px 0 8px" },
  successIcon: {
    width: "48px",
    height: "48px",
    lineHeight: "48px",
    borderRadius: "50%",
    background: "#e6f7ed",
    color: "#1a9e56",
    fontSize: "22px",
    margin: "0 auto 16px",
  },
  successMessage: {
    color: "#111827",
    fontSize: "17px",
    fontWeight: 700,
    margin: 0,
  },
  successSubtext: {
    color: "#6b7280",
    fontSize: "14px",
    marginTop: "6px",
  },
  form: { display: "flex", flexDirection: "column" },
  cardElementContainer: {
    border: "1px solid #e5e7eb",
    padding: "12px",
    borderRadius: "10px",
    marginBottom: "16px",
    backgroundColor: "#fff",
  },
  button: {
    padding: "13px",
    fontSize: "15px",
    fontWeight: 700,
    color: "#fff",
    backgroundColor: ACCENT,
    border: "none",
    borderRadius: "10px",
    cursor: "pointer",
    transition: "background 0.2s ease, transform 0.05s ease",
  },
  error: {
    textAlign: "left",
    color: "#dc2626",
    fontSize: "14px",
    marginTop: "8px",
  },
};

export default PaymentPage;
